import { useDispatch, useSelector } from 'react-redux'
import { captureException } from 'utils/sentry'
import { getVariables } from 'api/presets'
import { useFilterPresetSaveMutation } from 'api/hooks'
import { useGetPresets } from 'components/Common/FilterPresets/_methods'
import { useMyWinesFiltersInputs } from 'components/Pages/MyWines/Wines/_methods'
import type { RootState } from 'store'
import { getLocaleId } from 'utils/getLocaleId'
import {
  useDatabaseFiltersInputs,
  useELabelsFiltersInputs,
  useMyReviewsFiltersInputs,
} from 'utils/hooks'
import { useFilterStoreData } from 'utils/hooks/useFilterStoreData'
import { useTableFiltersInputs } from 'utils/hooks/useTableFiltersInputs'
import { useAdministrationFilterInputs } from 'utils/hooks/useAdministrationFiltersInputs'
import { FilterPresetEntity } from 'api/schemas'
import { useCapturedWinesFiltersInputs } from 'components/Pages/CapturedWines/_methods'

export const preparePageName = (page: string) => {
  const exceptions = {
    MyWinesWinesAll: 'myWinesWines',
    MyWinesWinesShortlist: 'myWinesWines',
    CapturedWinesAll: 'capturedWines',
    CapturedWinesShortlist: 'capturedWines',
  }
  let slicedName = page.substring(3)
  return (
    exceptions[slicedName] ||
    slicedName.charAt(0).toLowerCase() + slicedName.slice(1)
  )
}

interface IGetTableFiltersProps {
  entity: string
  storeName: string
}

const useGetTableFilters = ({
  entity,
  storeName,
}: IGetTableFiltersProps): any => {
  if (
    [
      'accMyWinesWines',
      'accMyWinesWinesAll',
      'accMyWinesWinesShortlist',
    ].includes(storeName)
  ) {
    // у это таблицы ENTITY = PRODUCT, но фильтры другие
    return useMyWinesFiltersInputs()
  }
  if (storeName === 'accAdministrationELabels') {
    return useAdministrationFilterInputs()
  }
  if (storeName === 'accRetailGeoIndications') {
    return useTableFiltersInputs()
  }
  switch (entity) {
    case FilterPresetEntity.WineReview:
      return useMyReviewsFiltersInputs()
    case FilterPresetEntity.CapturedWine:
      return useCapturedWinesFiltersInputs()
    case FilterPresetEntity.WineOffer:
    case FilterPresetEntity.Product:
    case FilterPresetEntity.Merchant:
    case FilterPresetEntity.TradeOfferLot:
      return useTableFiltersInputs()
    case FilterPresetEntity.TradeName:
    case FilterPresetEntity.Brand:
    case FilterPresetEntity.Wine:
    case FilterPresetEntity.GeoIndication:
    case FilterPresetEntity.Exhibition:
    case FilterPresetEntity.GeoIndicationCondition:
    case FilterPresetEntity.SpecialClassificationCondition:
      return useDatabaseFiltersInputs()
    case FilterPresetEntity.TradeOffer:
    case FilterPresetEntity.WineOfferRequest:
    case FilterPresetEntity.PriceSource:
    case FilterPresetEntity.Mention:
      return useAdministrationFilterInputs()
    case FilterPresetEntity.ProductELabel:
      return useELabelsFiltersInputs()
    default:
      return {}
  }
}

export const useGetCurrenEntity = ({ storeName }: { storeName: string }) => {
  switch (storeName) {
    case 'accSellWineMyOffers':
    case 'accBuyWineOffers':
    case 'accBuyWineAnalysis':
    case 'accAdministrationOffers':
      return FilterPresetEntity.WineOffer
    case 'accAdministrationRequests':
      return FilterPresetEntity.WineOfferRequest
    case 'accDatabaseBrands':
      return FilterPresetEntity.Brand
    case 'accAdministrationReviews':
    case 'accReviewsMyReviews':
      return FilterPresetEntity.WineReview
    case 'accDatabaseWines':
      return FilterPresetEntity.Wine
    case 'accDatabaseGeoIndication':
    case 'accRetailGeoIndications':
      return FilterPresetEntity.GeoIndication
    case 'accDatabaseProducers':
    case 'accDatabaseProducersDuplicates':
      return FilterPresetEntity.TradeName
    case 'accDatabaseKindOfWine':
      return FilterPresetEntity.GeoIndicationCondition
    case 'accDatabaseSpecialClassificationCondition':
      return FilterPresetEntity.SpecialClassificationCondition
    case 'accBuyWineAnalysisRating':
    case 'accBuyWineWines':
    case 'accRetailBuyersWine':
      return FilterPresetEntity.Product
    case 'accDatabaseExhibitions':
      return FilterPresetEntity.Exhibition
    case 'accELabels':
    case 'accAdministrationELabels':
      return FilterPresetEntity.ProductELabel
    case 'accAdministrationRetailOffers':
      return FilterPresetEntity.TradeOfferLot
    case 'accAdministrationRetailPublicPages':
      return FilterPresetEntity.TradeOffer
    case 'accAdministrationPriceSources':
      return FilterPresetEntity.PriceSource
    case 'accAdministrationMentions':
    case 'accAdministrationMentionsPrices':
      return FilterPresetEntity.Mention
    case 'accMyWinesWines':
    case 'accMyWinesWinesAll':
    case 'accMyWinesWinesShortlist':
      return FilterPresetEntity.Product
    case 'accCapturedWines':
    case 'accCapturedWinesAll':
    case 'accCapturedWinesShortlist':
      return FilterPresetEntity.CapturedWine
    case 'accRetailEShops':
      return FilterPresetEntity.Merchant
    case 'accRetailWines':
      return FilterPresetEntity.TradeOfferLot
    case 'accAdministrationLinkMentions':
      return FilterPresetEntity.Mention
    case 'accRetailMyRange':
      return FilterPresetEntity.TradeOfferLot
    default:
      throw new TypeError(`unknown store name ${storeName}`)
  }
}

interface ISavePreset {
  entity: FilterPresetEntity
  storeName: string
  onClose: () => void
}

export const useSavePreset = ({ storeName, onClose, entity }: ISavePreset) => {
  const dispatch = useDispatch()
  const { setPresetActionType, setAppliedPresetActionType } =
    useFilterStoreData()
  const { getTableFilters } = useGetTableFilters({ entity, storeName })

  const filtersStore = useSelector(
    (state: RootState) => state?.tableFilters?.[storeName]?.filters
  )
  const [savePreset, { loading }] = useFilterPresetSaveMutation()

  const { getPresets } = useGetPresets()

  const handleSavePreset = async ({ name }) => {
    try {
      const response = await savePreset({
        variables: {
          localeId: getLocaleId(),
          input: {
            localeId: getLocaleId(),
            name,
            page: preparePageName(storeName),
            entity,
            isFavorite: false,
            filters: getTableFilters(filtersStore) ?? [],
          },
        },
      })

      if (response) {
        const preset = response?.data?.filterPresetSave?.record ?? null
        const presetSaved = {
          id: preset?.id,
          name: preset?.name,
          enitity: preset?.entity,
          isFavorite: preset?.isFavorite,
          filters: preset?.filters,
        }
        dispatch({
          type: setPresetActionType,
          payload: presetSaved,
        })
        dispatch({
          type: setAppliedPresetActionType,
          payload: presetSaved,
        })
      }
      await getPresets({
        variables: getVariables({ page: preparePageName(storeName) }),
      })
    } catch (e) {
      captureException(e)
    }

    onClose()
  }

  return { handleSavePreset, loading }
}
